const STORAGE_KEY = 'og-favorite-products';

/** @returns {{ id: string, handle: string }[]} */
function getFavorites() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch (error) {
    return [];
  }
}

/** @param {{ id: string, handle: string }[]} favorites */
function setFavorites(favorites) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  } catch (error) {
    // localStorage unavailable (e.g. private browsing) - favorites just won't persist
  }
}

/**
 * A custom element wrapping a heart toggle button that saves/unsaves a product
 * to a browser-local favorites list (persisted in localStorage for the current browser).
 */
class ProductFavoriteButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.productId = this.button.dataset.productId;
    this.productHandle = this.button.dataset.productHandle;
    this.#updateState(getFavorites().some((item) => item.id === this.productId));

    this.button.addEventListener('click', this.#handleClick);
    document.addEventListener('product-favorite:change', this.#handleExternalChange);
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.#handleClick);
    document.removeEventListener('product-favorite:change', this.#handleExternalChange);
  }

  #updateState(isFavorited) {
    this.button.setAttribute('aria-pressed', isFavorited ? 'true' : 'false');
  }

  #handleClick = () => {
    const favorites = getFavorites();
    const index = favorites.findIndex((item) => item.id === this.productId);
    const isFavorited = index === -1;

    if (isFavorited) {
      favorites.push({ id: this.productId, handle: this.productHandle });
    } else {
      favorites.splice(index, 1);
    }

    setFavorites(favorites);
    this.#updateState(isFavorited);

    document.dispatchEvent(
      new CustomEvent('product-favorite:change', {
        detail: { productId: this.productId, isFavorited },
      })
    );
  };

  #handleExternalChange = (event) => {
    if (event.detail.productId !== this.productId) return;
    this.#updateState(event.detail.isFavorited);
  };
}

if (!customElements.get('product-favorite-button')) {
  customElements.define('product-favorite-button', ProductFavoriteButton);
}
